// Campaign KPI definitions + auto-measurement from tracked content.
// Shared by the create/edit modals (targets) and the campaign page (progress).
//
// Shape stored on Campaign.kpis (out-of-band JSONB):
//   {
//     sponsored: [{ metric, target }],           // project totals, paid content
//     organic:  { mode: 'total'|'monthly',       // total => endDate; monthly => 30-day windows
//                 startDate, endDate,
//                 kpis: [{ metric, target }] }    // non-paid content
//   }

import { reachOf, impressionsFor } from './reportMetrics';

const DAY = 24 * 60 * 60 * 1000;

// Available metrics. `kind` drives how the actual is computed + formatted.
export const KPI_METRICS = [
  { key: 'impressions',  label: 'Impressions',      kind: 'sum' },
  { key: 'views',        label: 'Views',            kind: 'sum' },
  { key: 'content',      label: 'Content pieces',   kind: 'count' },
  { key: 'engagement',   label: 'Engagement rate',  kind: 'rate', unit: '%' },
  { key: 'watch_hours',  label: 'Watch hours',      kind: 'sum' },
];

export const metricDef = (key) => KPI_METRICS.find(m => m.key === key) || KPI_METRICS[0];

export const emptyKpis = () => ({
  sponsored: [],
  organic: { mode: 'total', startDate: '', endDate: '', kpis: [] },
});

// Normalize whatever came back from the API into a complete, safe shape.
export const normalizeKpis = (raw) => {
  const base = emptyKpis();
  if (!raw || typeof raw !== 'object') return base;
  return {
    sponsored: Array.isArray(raw.sponsored) ? raw.sponsored : [],
    organic: {
      mode: raw.organic?.mode === 'monthly' ? 'monthly' : 'total',
      startDate: raw.organic?.startDate || '',
      endDate: raw.organic?.endDate || '',
      kpis: Array.isArray(raw.organic?.kpis) ? raw.organic.kpis : [],
    },
  };
};

export const hasSponsoredKpis = (type) => type === 'sponsored' || type === 'sponsored_organic';
export const hasOrganicKpis = (type) => type === 'organic' || type === 'sponsored_organic';

// True when any KPI has been configured (used to decide whether to show the section on the campaign page).
export const kpisConfigured = (kpis) => {
  const k = normalizeKpis(kpis);
  return (k.sponsored.length > 0) || (k.organic.kpis.length > 0);
};

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : parseFloat(v) || 0);
// Reach = streams by VOD views, else views — same model as the campaign/report
// summaries so KPI actuals match the ROI boxes.
const reachViews = (c) => reachOf(c);
const engagements = (c) => num(c.likes) + num(c.comments) + num(c.shares);

export const contentDate = (c) => {
  const d = c.publishedAt || c.createdAt || c.streamDate || c.contentDate;
  return d ? new Date(d) : null;
};

// Compute a metric's actual value from a list of content pieces.
export const computeMetric = (metricKey, list) => {
  switch (metricKey) {
    // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is estimated
    // at 5x rather than 10x, so multiplying a summed view total is wrong.
    case 'impressions':  return list.reduce((s, c) => s + impressionsFor(c, reachViews(c)), 0);
    case 'views':        return list.reduce((s, c) => s + reachViews(c), 0);
    case 'content':      return list.length;
    case 'watch_hours':  return list.reduce((s, c) => s + num(c.watchHours), 0);
    case 'engagement': {
      const reach = list.reduce((s, c) => s + reachViews(c), 0);
      const eng = list.reduce((s, c) => s + engagements(c), 0);
      return reach > 0 ? (eng / reach) * 100 : 0;
    }
    default: return 0;
  }
};

// Resolve the active organic window given the mode + dates.
// monthly => the 30-day window containing `now` (or the latest one if past the end).
// total   => the whole [startDate, endDate] range (either bound optional).
export const organicWindow = (organic, now = new Date()) => {
  if (!organic) return null;
  if (organic.mode === 'monthly') {
    if (!organic.startDate) return null;
    const start = new Date(organic.startDate);
    if (isNaN(start)) return null;
    const idx = Math.max(0, Math.floor((now - start) / (30 * DAY)));
    const winStart = new Date(start.getTime() + idx * 30 * DAY);
    const winEnd = new Date(winStart.getTime() + 30 * DAY);
    return { mode: 'monthly', monthNumber: idx + 1, start: winStart, end: winEnd };
  }
  return {
    mode: 'total',
    start: organic.startDate ? new Date(organic.startDate) : null,
    end: organic.endDate ? new Date(organic.endDate) : null,
  };
};

const inWindow = (c, win) => {
  if (!win) return true;
  const d = contentDate(c);
  if (!d) return true; // undated content counts (rare)
  if (win.start && d < win.start) return false;
  if (win.end && d > win.end) return false;
  return true;
};

// Compute full progress for the campaign page.
// Returns { sponsored: [row], organic: { window, rows: [row] } }
// row = { metric, label, unit, target, actual, pct }
export const computeKpiProgress = (kpis, contents, now = new Date()) => {
  const k = normalizeKpis(kpis);
  const paid = (contents || []).filter(c => c.isPaid === true);
  const organic = (contents || []).filter(c => c.isPaid !== true);

  const rowFor = (kpi, list) => {
    const def = metricDef(kpi.metric);
    const target = num(kpi.target);
    const actual = computeMetric(kpi.metric, list);
    const rawPct = target > 0 ? (actual / target) * 100 : null; // uncapped attainment
    const pct = rawPct != null ? Math.min(100, rawPct) : 0;      // capped, for the bar width
    return { metric: kpi.metric, label: def.label, unit: def.unit || '', target, actual, pct, rawPct };
  };

  const sponsored = k.sponsored.map(kpi => rowFor(kpi, paid));

  const win = organicWindow(k.organic, now);
  const organicList = organic.filter(c => inWindow(c, win));
  const organicRows = k.organic.kpis.map(kpi => rowFor(kpi, organicList));

  return {
    sponsored,
    organic: { mode: k.organic.mode, window: win, rows: organicRows },
  };
};

// Display formatting for a KPI value. Full numbers with thousands separators
// (rates as a percentage).
export const formatKpiValue = (val, metricKey) => {
  const def = metricDef(metricKey);
  if (def.kind === 'rate') return `${(num(val)).toFixed(1)}%`;
  return Math.round(num(val)).toLocaleString();
};
